import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useHub } from "../../../core/Global";
import Icon from "../../../components/base/Icon";
import Indicator from "../../../components/base/Indicator";
import FlexRow from "../../../components/base/FlexRow";
import { FontAwesomeIcons } from "../../../core/Types";

interface SharedReportFile {
    name?: string;
    type?: string;
    size?: number;
    downloadUrl?: string;
}

interface SharedReportModel {
    reportName?: string;
    description?: string;
    period?: string;
    createdAt?: string;
    companyName?: string;
    files?: SharedReportFile[];
}

function formatSize(bytes?: number): string {
    if (!bytes || bytes <= 0) return "";
    const units = ["B", "KB", "MB", "GB"];
    let n = bytes;
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(i === 0 || n >= 10 ? 0 : 1)} ${units[i]}`;
}

function fileIcon(type?: string): FontAwesomeIcons {
    if (type === "pdf") return "file-pdf";
    if (type === "csv") return "file-csv";
    if (type === "excel") return "file-excel";
    return "file";
}

export default function ReportView() {

    const { owner, id, token } = useParams();
    const hub = useHub();

    const [model, modelSet] = useState<SharedReportModel>();
    const [loading, loadingSet] = useState<boolean>(true);
    const [notFound, notFoundSet] = useState<boolean>(false);

    useEffect(() => {
        hub.Get(`/base/public/share/report/${owner}/${id}/${token}`, res => {
            if (res.success && res.data) modelSet(res.data);
            else notFoundSet(true);
            loadingSet(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <div className="py-20">
                <Indicator text="Loading..." />
            </div>
        );
    }

    if (notFound || !model) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-center py-20">
                <Icon icon="link-slash" type="solid" size={42} className="text-gray-400 mb-4" />
                <div className="fs-3 fw-bold text-gray-800">This link is not available</div>
                <div className="fs-6 text-gray-600 mt-2">
                    The link may be invalid or has expired. Please ask the sender for a new link.
                </div>
            </div>
        );
    }

    const created = model.createdAt ? new Date(model.createdAt).toLocaleString() : "";

    return (
        <div className="mx-auto" style={{ maxWidth: 720 }}>

            <div className="card shadow-sm border">
                <div className="card-body p-8">

                    <FlexRow className="mb-6">
                        <div className="symbol symbol-50px me-4">
                            <span className="symbol-label bg-light-primary">
                                <Icon icon="file-export" type="solid" size={22} className="text-primary" />
                            </span>
                        </div>
                        <div>
                            <div className="fs-2 fw-bolder text-gray-900">{model.reportName ?? "Shared Files"}</div>
                            {
                                model.companyName &&
                                <div className="fs-6 text-gray-600">{model.companyName}</div>
                            }
                        </div>
                    </FlexRow>

                    {
                        model.description &&
                        <div className="fs-6 text-gray-700 mb-6" style={{ whiteSpace: "pre-line" }}>
                            {model.description}
                        </div>
                    }

                    <div className="separator separator-dashed my-6"></div>

                    <div className="row mb-8">
                        {
                            model.period &&
                            <div className="col-sm-6 mb-4 mb-sm-0">
                                <div className="fs-8 text-gray-500 text-uppercase fw-bold mb-1">Period</div>
                                <FlexRow>
                                    <Icon icon="calendar-range" className="text-gray-500 me-2" size={13} />
                                    <span className="fs-6 fw-semibold text-gray-800">{model.period}</span>
                                </FlexRow>
                            </div>
                        }
                        {
                            created &&
                            <div className="col-sm-6">
                                <div className="fs-8 text-gray-500 text-uppercase fw-bold mb-1">Generated</div>
                                <FlexRow>
                                    <Icon icon="clock" className="text-gray-500 me-2" size={13} />
                                    <span className="fs-6 fw-semibold text-gray-800">{created}</span>
                                </FlexRow>
                            </div>
                        }
                    </div>

                    <div className="fs-8 text-gray-500 text-uppercase fw-bold mb-3">Files</div>

                    {
                        (model.files?.length ?? 0) === 0 ?
                            <div className="text-gray-500 fs-7">No files are available for this report.</div>
                            :
                            model.files!.map((f, k) =>
                                <a key={k} href={f.downloadUrl} target="_blank" rel="noreferrer"
                                    className="d-flex align-items-center border rounded p-4 mb-3 text-gray-800 text-hover-primary bg-hover-light">
                                    <Icon icon={fileIcon(f.type)} type="solid" size={22} className="text-primary me-4" />
                                    <div className="flex-grow-1">
                                        <div className="fs-6 fw-bold">{f.name ?? ("Report." + (f.type ?? "file"))}</div>
                                        <div className="fs-8 text-gray-500 text-uppercase">
                                            {f.type}{formatSize(f.size) ? " · " + formatSize(f.size) : ""}
                                        </div>
                                    </div>
                                    <Icon icon="arrow-down-to-line" type="solid" size={16} className="text-gray-500" />
                                </a>
                            )
                    }

                </div>
            </div>

            <div className="text-center text-gray-400 fs-8 mt-4">
                This is a private link. Please do not share it.
            </div>

        </div>
    );
}
